import { useQueries, useQuery } from '@tanstack/react-query';
import { getChart, getGameServerMetrics, getTimeWindows } from './api';
import { getMonitorApiBaseUrl } from '../../config/env';
import { CHARTS, GAME_SERVER_HEARTBEAT_CHART_ID, TimeFilter } from './types';

export function useTimeWindows() {
  const monitorBaseUrl = getMonitorApiBaseUrl();
  return useQuery({
    queryKey: ['monitor-windows', monitorBaseUrl],
    queryFn: ({ signal }) => getTimeWindows(signal),
    enabled: Boolean(monitorBaseUrl),
    staleTime: Infinity,
    retry: false,
  });
}

export function useMonitorCharts(filter: TimeFilter | undefined, refresh: number) {
  const monitorBaseUrl = getMonitorApiBaseUrl();
  const first = useQuery({
    queryKey: ['monitor-chart', monitorBaseUrl, 'host-cpu', filter, refresh],
    queryFn: ({ signal }) => getChart('host-cpu', filter!, signal),
    enabled: Boolean(monitorBaseUrl && filter),
    retry: false,
    staleTime: Infinity,
    refetchInterval: 60000,
  });
  const range = first.data && { from: first.data.from, to: first.data.to, interval: first.data.interval };
  const rest = useQueries({ queries: CHARTS.slice(1).map(({ id }) => ({
    queryKey: ['monitor-chart', monitorBaseUrl, id, filter, refresh, range],
    queryFn: ({ signal }: { signal: AbortSignal }) => getChart(id, range!, signal),
    enabled: Boolean(range),
    retry: false,
    staleTime: Infinity,
    refetchInterval: 60000,
  })) });
  return [first, ...rest];
}

export function useGameServerMetrics(filter: TimeFilter | undefined, refresh: number) {
  const monitorBaseUrl = getMonitorApiBaseUrl();
  return useQuery({
    queryKey: ['game-server-metrics', monitorBaseUrl, filter, refresh],
    queryFn: ({ signal }) => getGameServerMetrics(filter!, '', signal),
    enabled: Boolean(monitorBaseUrl && filter),
    retry: false,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

export function useGameServerHeartbeats(filter: TimeFilter | undefined, refresh: number) {
  const monitorBaseUrl = getMonitorApiBaseUrl();
  return useQuery({
    queryKey: ['monitor-chart', monitorBaseUrl, GAME_SERVER_HEARTBEAT_CHART_ID, filter, refresh],
    queryFn: ({ signal }) => getChart(GAME_SERVER_HEARTBEAT_CHART_ID, filter!, signal),
    enabled: Boolean(monitorBaseUrl && filter),
    retry: false,
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

export const DEFAULT_REFRESH_INTERVAL_MS = 5000;
export const REFRESH_INTERVAL_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 5000, label: '5s' },
  { value: 10000, label: '10s' },
  { value: 30000, label: '30s' },
  { value: 60000, label: '1m' },
];
